import React, { useEffect, useRef, useState } from 'react';
import { Upload } from 'lucide-react';

interface FileDropZoneProps {
  theme: 'light' | 'dark';
  /** Panel name shown in the overlay, e.g. "Original". */
  label: string;
  /** Dropped files, in drop order. Reading happens in the parent (see utils/fileLoader). */
  onFiles: (files: File[]) => void;
  /** Plain text dragged from another app or tab. */
  onText: (text: string) => void;
  children: React.ReactNode;
  className?: string;
}

const hasDraggableContent = (event: React.DragEvent) => {
  const types = Array.from(event.dataTransfer?.types ?? []);
  return types.includes('Files') || types.includes('text/plain');
};

/**
 * Drop target for a diff panel. Uses an enter/leave depth counter so the
 * highlight does not flicker while the pointer crosses child elements, and
 * a pointer-events-none overlay so it never swallows the drop itself.
 */
export const FileDropZone: React.FC<FileDropZoneProps> = ({ theme, label, onFiles, onText, children, className = '' }) => {
  const isDark = theme === 'dark';
  const [isDragging, setIsDragging] = useState(false);
  const depth = useRef(0);

  const onDragEnter = (event: React.DragEvent) => {
    if (!hasDraggableContent(event)) return;
    event.preventDefault();
    depth.current += 1;
    setIsDragging(true);
  };

  const onDragOver = (event: React.DragEvent) => {
    if (!hasDraggableContent(event)) return;
    event.preventDefault();
    event.dataTransfer.dropEffect = 'copy';
  };

  const onDragLeave = () => {
    depth.current = Math.max(0, depth.current - 1);
    if (depth.current === 0) setIsDragging(false);
  };

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    depth.current = 0;
    setIsDragging(false);
    const files = Array.from(event.dataTransfer.files ?? []);
    if (files.length > 0) {
      onFiles(files);
      return;
    }
    const text = event.dataTransfer.getData('text/plain');
    if (text) onText(text);
  };

  return (
    <div
      className={`relative ${className}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {children}
      {isDragging && (
        <div
          aria-hidden
          className={`pointer-events-none absolute inset-0 z-20 m-2 flex items-center justify-center rounded-lg border-2 border-dashed animate-fade-in ${
            isDark ? 'bg-blue-500/10 border-blue-400' : 'bg-blue-50/90 border-blue-500'
          }`}
        >
          <div className="flex flex-col items-center gap-2 text-center px-4">
            <Upload size={32} className="text-blue-500" />
            <span className="text-sm font-bold text-blue-500">Drop to load into {label}</span>
            <span className={`text-[11px] ${isDark ? 'text-slate-400' : 'text-slate-500'}`}>
              Drop two files to compare them at once. Files are read locally.
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Stop the browser from navigating to a file dropped outside a drop zone,
 * which would silently discard the user's workspace.
 */
export function usePreventFileNavigation(): void {
  useEffect(() => {
    const prevent = (event: DragEvent) => {
      if (event.dataTransfer && Array.from(event.dataTransfer.types).includes('Files')) {
        event.preventDefault();
      }
    };
    window.addEventListener('dragover', prevent);
    window.addEventListener('drop', prevent);
    return () => {
      window.removeEventListener('dragover', prevent);
      window.removeEventListener('drop', prevent);
    };
  }, []);
}

export default FileDropZone;
